/**
 * PlatformConnectionsPage — /organization/platforms
 * Intel platforms in a list: category filter, status, limits, a connection test and an on/off switch per row.
 * Demo pages: /connected-apps (rows with a status badge and a connect/disconnect control), /tables/data-tables.
 * Template example: NextAdmin `(dashboard)/(home)/_component/last-transactions-table`. Creating a platform is the
 * page /organization/platforms/new. Server routes: all `CanManageIntegrations`.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plug, Plus, Wifi } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  listIntelPlatformsOptions,
  listIntelPlatformsQueryKey,
  testIntelPlatformMutation,
  updateIntelPlatformMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { IntelPlatform, IntelPlatformTestResult } from '../../generated/api/types.gen.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { ListPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { Badge, type BadgeVariant } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Callout } from '../../components/ui/Callout.js';
import { DataTable, type DataTableColumn } from '../../components/ui/DataTable.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { FilterPills } from '../../components/ui/FilterPills.js';
import { SearchBar } from '../../components/ui/SearchBar.js';
import { Toggle } from '../../components/ui/Toggle.js';
import { Toolbar } from '../../components/ui/Toolbar.js';
import { useToast } from '../../components/ui/Toast.js';
import { PAGE_HELP } from '../../help/index.js';
import { PLATFORM_CATEGORIES, platformCategoryLabel } from './platformLabels.js';

const TEST_BADGE: Record<IntelPlatformTestResult['status'], { variant: BadgeVariant; label: string }> = {
  connected: { variant: 'success', label: 'Connected' },
  partial: { variant: 'warning', label: 'Partly set up' },
  disconnected: { variant: 'danger', label: 'Not connected' },
};

export const PlatformConnectionsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const query = useQuery(listIntelPlatformsOptions());
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [testResults, setTestResults] = useState<Record<string, IntelPlatformTestResult>>({});

  const test = useMutation({
    ...testIntelPlatformMutation(),
    onSuccess: (result, variables) => {
      const slug = variables.path.slug;
      setTestResults((previous) => ({ ...previous, [slug]: result }));
    },
    onError: (error) => notify(error.message, 'danger'),
  });
  const toggle = useMutation({
    ...updateIntelPlatformMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listIntelPlatformsQueryKey() }),
    onError: (error) => notify(error.message, 'danger'),
  });

  const platforms = query.data?.data ?? [];
  const needle = search.trim().toLowerCase();
  const rows = platforms.filter(
    (platform) =>
      (category === 'all' || platform.category === category) &&
      (needle === '' || platform.name.toLowerCase().includes(needle) || platform.slug.toLowerCase().includes(needle)),
  );
  const pills = [
    { id: 'all', label: 'All', count: platforms.length },
    ...PLATFORM_CATEGORIES.filter((id) => platforms.some((platform) => platform.category === id)).map((id) => ({
      id,
      label: platformCategoryLabel(id),
      count: platforms.filter((platform) => platform.category === id).length,
    })),
  ];

  const columns: DataTableColumn<IntelPlatform>[] = [
    {
      key: 'platform',
      header: 'Platform',
      width: '28%',
      render: (platform) => (
        <div>
          <p className="text-[13px] font-semibold text-heading">{platform.name}</p>
          {platform.config?.api_key_env && (
            <p className="mt-[3px] font-mono text-[11px] text-muted">{platform.config.api_key_env}</p>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (platform) => <Badge variant="neutral">{platformCategoryLabel(platform.category)}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (platform) => {
        const result = testResults[platform.slug];
        if (result) {
          const badge = TEST_BADGE[result.status];
          return <Badge variant={badge.variant}>{badge.label}</Badge>;
        }
        return platform.enabled ? (
          <Badge variant="success" dot>
            Enabled
          </Badge>
        ) : (
          <Badge variant="neutral" dot>
            Off
          </Badge>
        );
      },
    },
    {
      key: 'limits',
      header: 'Requests per minute',
      render: (platform) => (
        <span className="text-sm text-body">{platform.config?.rate_limit_per_minute ?? 'No limit'}</span>
      ),
    },
    {
      key: 'quota',
      header: 'Daily quota',
      render: (platform) => (
        <span className="text-sm text-body">{platform.config?.rate_limit_per_day ?? 'No limit'}</span>
      ),
    },
    {
      key: 'lookback',
      header: 'Lookback',
      render: (platform) => <span className="text-sm text-body">{platform.defaultLookbackDays ?? 30} days</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '240px',
      render: (platform) => (
        <Can policy="CanManageIntegrations">
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="ghost"
              size="sm"
              loading={test.isPending && test.variables?.path.slug === platform.slug}
              onClick={() => test.mutate({ path: { slug: platform.slug } })}
            >
              <Wifi size={14} aria-hidden="true" />
              Test
            </Button>
            <Toggle
              checked={platform.enabled}
              aria-label={`${platform.enabled ? 'Turn off' : 'Turn on'} ${platform.name}`}
              disabled={toggle.isPending}
              onChange={(enabled) => toggle.mutate({ path: { slug: platform.slug }, body: { enabled } })}
            />
          </div>
        </Can>
      ),
    },
  ];

  const newLink = (
    <Can policy="CanManageIntegrations">
      <Link
        to="/organization/platforms/new"
        className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}
      >
        <Plus size={14} aria-hidden="true" />
        Add platform
      </Link>
    </Can>
  );

  const help = PAGE_HELP['platform-connections'];

  return (
    <ListPage
      title="Platform connections"
      subtitle="Set the keys, limits and fetch rules Conflui uses for each data platform."
      helpContent={help}
      actions={newLink}
      alert={
        query.isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load platform connections"
            actions={
              <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
                Retry
              </Button>
            }
          >
            Check your connection and try again.
          </Callout>
        ) : undefined
      }
      toolbar={
        <Toolbar
          search={<SearchBar value={search} onChange={setSearch} placeholder="Search platforms…" />}
          pills={<FilterPills aria-label="Category" options={pills} value={category} onChange={setCategory} />}
        />
      }
    >
      {!query.isError && (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(platform) => platform.slug}
          loading={query.isPending}
          aria-label="Platform connections"
          empty={
            <EmptyState
              icon={Plug}
              title={help?.emptyState.title ?? 'No platforms found'}
              description={help?.emptyState.description ?? 'Add a platform to start collecting data.'}
              learnItems={help?.emptyState.learnItems}
              action={newLink}
            />
          }
        />
      )}
    </ListPage>
  );
};
